import { useState, type ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Box, ToggleButtonGroup } from '@mui/material'
import { useGetInnerForcesEnvelope } from '@api/inner-forces-envelope/inner-forces-envelope'
import {
  envelopeComponentLabels,
  type EnvelopeComponent,
} from '@editor/pages/Results2/components/Results2Scene/components/InnerForcesEnvelopeMesh/utils'
import { TooltipToggleButton } from '@ui2/forms'
import { Section } from '@ui2/structure'
import { useSnackbar } from '@ui/snackbar'
import { useEditorViewStore } from '@editorStores'
import InnerForcesEnvelopeSceneEffect from './InnerForcesEnvelopeSceneEffect'

const envelopeComponents: EnvelopeComponent[] = ['moment_y', 'shear_z', 'displacement_z']

const envelopeComponentTooltips: Record<EnvelopeComponent, string> = {
  moment_y: 'Umhüllende des Biegemoments My in der 3D-Ansicht ein-/ausblenden',
  shear_z: 'Umhüllende der Querkraft Vz in der 3D-Ansicht ein-/ausblenden',
  displacement_z:
    'Umhüllende der Durchbiegung uz (charakteristische Kombinationen) in der 3D-Ansicht ein-/ausblenden',
}

const loadTypeToggles = [
  {
    loadType: 'line-load' as const,
    label: 'Linienl.',
    tooltip: 'Linienlasten des Bauteils in der 3D-Ansicht ein-/ausblenden',
  },
  {
    loadType: 'point-load' as const,
    label: 'Punktl.',
    tooltip: 'Punktlasten des Bauteils in der 3D-Ansicht ein-/ausblenden',
  },
]

/**
 * Title with toggles for the selected member's 3D view: its line and point loads (shared with the
 * scene's load type switches) and the My / Vz design inner forces or uz deflection envelope. No envelope is
 * selected (and nothing loaded) at first; clicking the selected component again hides it.
 */
export const InnerForcesSection = ({ elementId }: { elementId?: string }): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [envelopeComponent, setEnvelopeComponent] = useState<EnvelopeComponent | null>(null)
  const visibleLoadTypes = useEditorViewStore(state => state.results.visibleLoadTypes)
  const setVisibleLoadType = useEditorViewStore(state => state.setResultsVisibleLoadType)

  const { data: envelope } = useGetInnerForcesEnvelope(projectId as string, elementId as string, {
    query: {
      enabled: !!envelopeComponent && !!projectId && !!elementId,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Umhüllenden'),
          { variant: 'error', preventDuplicate: true, key: 'inner-forces-envelope-error' },
        ),
    },
  })

  const actions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        size="small"
        value={loadTypeToggles
          .filter(({ loadType }) => visibleLoadTypes[loadType])
          .map(({ loadType }) => loadType)}
        data-cy="inner-forces-load-type-toggle"
      >
        {loadTypeToggles.map(({ loadType, label, tooltip }) => (
          <TooltipToggleButton
            key={loadType}
            value={loadType}
            tooltip={tooltip}
            onClick={() => setVisibleLoadType(loadType, !visibleLoadTypes[loadType])}
          >
            {label}
          </TooltipToggleButton>
        ))}
      </ToggleButtonGroup>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={envelopeComponent}
        onChange={(_, next: EnvelopeComponent | null) => setEnvelopeComponent(next)}
        data-cy="inner-forces-envelope-component-toggle"
      >
        {envelopeComponents.map(component => (
          <TooltipToggleButton
            key={component}
            value={component}
            tooltip={envelopeComponentTooltips[component]}
          >
            {envelopeComponentLabels[component]}
          </TooltipToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )

  return (
    <>
      <Section title="Bemessungsschnittgrößen" hideBorderBottom wrapActions actions={actions} />
      {envelope && envelopeComponent && (
        <InnerForcesEnvelopeSceneEffect envelope={envelope} component={envelopeComponent} />
      )}
    </>
  )
}
